import {
  resultImage1,
  resultImage2,
  resultImage3,
  resultImage4,
} from "../../assets/realResultImages";
import essencaLogo from "../../assets/essenca-optimized.png";
import estethicalLogo from "../../assets/estethica-optimized.png";
import getcoLogo from "../../assets/getco-optimized.png";
import metahomeLogo from "../../assets/metahome-optimized.png";
import technohomeLoogo from "../../assets/technohome-optimized.png";

const homeEN = {
  nav: {
    services: "Services",
    process: "Process",
    results: "Results",
    pricing: "Pricing",
    faq: "FAQ",
    contact: "Contact",
    pages: "Pages",
  },
  seo: {
    title: "Custom Software & Web Development Company | QuadroCode",
    description:
      "QuadroCode builds scalable websites, web apps, mobile apps, and custom software with clean UX, reliable architecture, and ongoing support.",
  },
  home: {
    hero: {
      badge: "#Software Development Company",
      titleLine1: "Innovate, Build, Scale —",
      titleLine2: "Your Vision, Our Code",
      descriptionLine1:
        "We build websites, web apps, and custom software that help your business grow.",
      descriptionLine2:
        "Clean UI/UX, scalable architecture, and reliable delivery — from simple sites to complex platforms.",
      cta: "Get Started",
      checks: ["Web App Development", "Website & CMS","Mobile App Development"],
    },

    services: {
      titleLine1: "Software Solutions,",
      titleLine2: "Built to Scale",
      subtitleLine1:
        "From websites to custom platforms — we deliver clean UI/UX, solid architecture,",
      subtitleLine2:
        "and dependable execution that supports real business growth.",
      cardCta: "Talk to Us",
      sectionCta: "View More",
      cards: [
        {
          id: 1,
          variant: "ux",
          title: "UI/UX Design",
          points: [
            "User-first interfaces that improve clarity and conversion.",
            "Design systems that stay consistent as you grow.",
            "Responsive layouts that work across devices and screen sizes.",
          ],
        },
        {
          id: 2,
          variant: "webapp",
          title: "Web App Development",
          points: [
            "Dashboards, portals, and SaaS apps with clean structure.",
            "Secure APIs and scalable backend foundations.",
            "Performance-focused builds with maintainable codebases.",
          ],
        },
        {
          id: 3,
          variant: "cms",
          title: "Website & CMS",
          points: [
            "Fast websites designed to be SEO-ready from day one.",
            "Easy content management for non-technical teams.",
            "Flexible pages built to support future expansion.",
          ],
        },
      ],
      extraCards: [
        {
          id: 4,
          variant: "perf",
          title: "Performance Optimization",
          points: [
            "Speed improvements for Core Web Vitals and UX.",
            "Asset, image, and render optimization where it matters.",
            "Practical fixes that reduce load time and bounce rate.",
          ],
        },
        {
          id: 5,
          variant: "support",
          title: "Maintenance & Support",
          points: [
            "Ongoing updates, monitoring, and small improvements.",
            "Bug fixes with clear reporting and priorities.",
            "Stable releases without breaking production.",
          ],
        },
        {
          id: 6,
          variant: "devops",
          title: "DevOps & Deployment",
          points: [
            "CI/CD foundations for reliable deployments.",
            "Linux server setup, domain, SSL, and monitoring.",
            "Production hardening and environment consistency.",
          ],
        },
        {
          id: 7,
          variant: "mobile",
          title: "Mobile Development",
          points: [
            "Cross-platform apps with clean UX and native performance feel.",
            "Structured architecture that stays maintainable as you scale.",
            "Auth, payments, push notifications, and store-ready delivery.",
          ],
        },
        {
          id: 8,
          variant: "api",
          title: "API Integration",
          points: [
            "Reliable integration with external APIs and internal services.",
            "Secure auth flows (JWT/OAuth) and clean error handling.",
            "Stable data sync, caching, and performance-first requests.",
          ],
        },
        {
          id: 9,
          variant: "ai",
          title: "AI Agents",
          points: [
            "Task-driven agents that automate workflows and operations.",
            "Tool + API connected agents with guardrails and logging.",
            "Deployable architecture designed for real usage at scale.",
          ],
        },
      ],
    },

    process: {
      titleLine1: "A Clear Process,",
      titleLine2: "Built for Delivery",
      subtitleLine1: "We keep things simple: understand the goal, design with purpose, build clean,",
      subtitleLine2: "and launch with confidence — then improve based on real feedback.",
      cards: [
        {
          number: "01",
          variant: "discover",
          title: "Discover & Scope",
          text: "We clarify goals, users, and requirements, then define a clean scope and timeline so there are no surprises.",
        },
        {
          number: "02",
          variant: "design",
          title: "Design & Prototype",
          text: "We create a focused UI/UX direction and prototype key flows to validate the experience early.",
        },
        {
          number: "03",
          variant: "build",
          title: "Build & Integrate",
          text: "We develop with scalable structure, clean APIs, and integrations that fit your business workflow.",
        },
        {
          number: "04",
          variant: "launch",
          title: "Test & Launch",
          text: "We test across devices, fix edge cases, and prepare for production with stable deployments and performance checks.",
        },
        {
          number: "05",
          variant: "grow",
          title: "Support & Improve",
          text: "After launch, we iterate: new features, performance improvements, and ongoing support to keep your product moving.",
        },
      ],
    },

    results: {
      titleLine1: "Real Work,",
      titleLine2: "Production-Ready Results",
      subtitleLine1: "We build practical solutions — clean UI, reliable functionality,",
      subtitleLine2: "and systems that are ready to scale with your business.",
      cta: "Contact Us",
      items: [
        {
          id: 1,
          imgURL: resultImage1.src,
          imgSrcSet: resultImage1.srcSet,
          title: "E-Commerce Website Foundation",
          text: "A fast, responsive storefront with clear structure, product pages, and an experience designed for conversion.",
        },
        {
          id: 2,
          imgURL: resultImage2.src,
          imgSrcSet: resultImage2.srcSet,
          title: "Team Collaboration Web App",
          text: "A secure internal tool concept with role-based access, real-time workflows, and scalable architecture.",
        },
        {
          id: 3,
          imgURL: resultImage3.src,
          imgSrcSet: resultImage3.srcSet,
          title: "Modern UI Refresh",
          text: "A UI overhaul approach focused on readability, consistency, and responsive behavior across devices.",
        },
        {
          id: 4,
          imgURL: resultImage4.src,
          imgSrcSet: resultImage4.srcSet,
          title: "Brand Showcase Website",
          text: "A clean marketing site structure that highlights services, builds trust, and drives contact requests.",
        },
      ],
    },

    pricing: {
      titleLine1: "Flexible Pricing,",
      titleLine2: "Built Around Your Scope",
      subtitle:
        "Choose a starting package and we’ll finalize the quote based on features, pages, integrations, and timeline.",
      tabs: ["Monthly", "Quarter", "Annual"],
      priceSuffix: "starting from",
      cta: "Get Started",
      note:
        "Final pricing depends on requirements. We provide a clear scope, timeline, and fixed quote after a short call.",
      plans: [
        {
          id: 1,
          variant: "starter",
          title: "Starter Website",
          desc:
            "Best for new businesses that need a fast, clean website to start receiving leads and building trust.",
          priceFrom: "$499",
          features: [
            "Modern responsive design",
            "Basic SEO setup (titles, meta, structure)",
            "Contact section + CTA flow",
            "Performance-friendly assets",
            "Easy future expansion",
          ],
        },
        {
          id: 2,
          variant: "business",
          title: "Business Website",
          desc:
            "Best for service companies that need multiple pages/sections, stronger content structure, and integrations.",
          priceFrom: "$1,299",
          features: [
            "Deeper content & section strategy",
            "Conversion-focused UX improvements",
            "Integrations (WhatsApp, forms, email)",
            "On-page SEO foundations",
            "Deployment & basic hardening",
          ],
        },       
        {
          id: 3,
          variant: "mobile",
          title: "Mobile App",
          desc:
            "Best for businesses that need a customer-facing or internal mobile app with strong UX, backend connectivity",
          priceFrom: "$2,499",
          features: [
            "iOS and Android delivery path",
            "Clean mobile UX and flows",
            "API/backend integration ready",
            "Auth, notifications, and core app logic",
            "Store launch support",
          ],
        },
         {
          id: 4,
          variant: "custom",
          title: "Custom Software",
          desc:
            "Best for web apps, portals, dashboards, automations, and platforms that require backend, roles, and APIs.",
          priceFrom: "Quote",
          features: [
            "Architecture + roadmap planning",
            "Backend APIs & integrations",
            "Roles/permissions and secure flows",
            "Scalable codebase and deployment",
            "Maintenance & iteration options",
          ],
        },
      ],
    },

    trusted: {
      title: "Trusted by Partners",
      aria: "Partner logos carousel",
      logoAlt: "Partner logo {n}",
      logos: [
        metahomeLogo,
        essencaLogo,
        technohomeLoogo,
        estethicalLogo,
        getcoLogo,
      ],
    },

    footer: {
      faq: {
        title: "Answers to Common Questions",
        subtitle: "Quick, clear answers about how we work and what you can expect.",
        cta: "Contact Us",
        items: [
          {
            id: 1,
            q: "How long does it take to build a website?",
            a: "Most websites take 1–3 weeks depending on pages, content readiness, and feedback speed. Larger sites or complex requirements take longer.",
          },
          {
            id: 2,
            q: "Can you build custom software (not just websites)?",
            a: "Yes. We build web apps, dashboards, portals, and automation tools — including backend APIs, roles/permissions, and integrations.",
          },
          {
            id: 3,
            q: "Do you help with website redesigns?",
            a: "Yes. We can refresh UI/UX, improve structure and performance, and keep what already works — without breaking your existing content strategy.",
          },
          {
            id: 4,
            q: "Do you provide maintenance and support?",
            a: "Yes. We offer ongoing support for updates, bug fixes, improvements, and monitoring — depending on what your product needs.",
          },
          {
            id: 5,
            q: "Are your websites mobile-friendly and fast?",
            a: "Yes. We build responsive layouts and optimize assets to support good performance and user experience across devices.",
          },
          {
            id: 6,
            q: "How do you price projects?",
            a: "We start from a base package and finalize the quote based on scope: pages, features, integrations, and timeline. You’ll get a clear scope and fixed quote.",
          },
        ],
      },

      cta: {
        title: "Start Your Project With QuadroCode",
        button: "Get Started",
      },

      brand: {
        name: "QuadroCode",
        logoAlt: "QuadroCode logo",
        taglineLine1: "Innovate, Build, Scale —",
        taglineLine2: "Your Vision, Our Code",
        contactBtn: "Contact Us",
      },

      contact: {
        // Fill these when ready (optional)
        emailLabel: "Email",
        phoneLabel: "Phone",
        email: "",      // e.g. "hello@quadrocode.com"
        phone: "",      // e.g. "+90..."
        location: "",   // e.g. "Istanbul, Türkiye"
        form: {
          title: "Tell Us About Your Project",
          subtitle: "Use the form to share your scope, timeline, or idea and our team will get back to you.",
          submit: "Send Inquiry",
          success: "Inquiry sent successfully.",
          error: "Something went wrong. Please try again.",
          sending: "Sending inquiry...",
          emailIntro: "New project inquiry from the website.",
          defaultSubject: "New project inquiry",
          fields: {
            name: "Name",
            email: "Email",
            subject: "Subject",
            message: "Message",
          },
          placeholders: {
            name: "Your name",
            email: "you@company.com",
            subject: "Project scope",
            message: "Tell us what you need built.",
          },
        },
      },

      socialsAria: "Social links",
      socials: {
        whatsapp: "",
        instagram: "",
        linkedin: "",
        x: "",
      },

      links: {
        col1: {
          title: "Menu",
          items: [
            { label: "Services", href: "#services" },
            { label: "Process", href: "#process" },
            { label: "Results", href: "#results" },
            { label: "Pricing", href: "#pricing" },
          ],
        },
        col2: {
          title: "Company",
          items: [
            { label: "FAQ", href: "#faq" },
            { label: "Contact", href: "#contact" },
          ],
        },
        col3: {
          title: "Services",
          items: [
            { label: "UI/UX Design", href: "#services" },
            { label: "Web Development", href: "#services" },
            { label: "Custom Software", href: "#services" },
          ],
        },
      },

      copyright:
        "Copyright © {year} QuadroCode — Software Development Company",
    },
  },
};

export default homeEN;
